/**
 * ProposalCard — the inbox (email-surface) propose-new-customer / ship-to card.
 *
 * Rendered in EmailViewModal's customer sidebar from `email.proposals`
 * (classification extra_data — see services/orders/proposals.py). The order
 * surface has its own create-it? cards inside OrderDetailModal reading the
 * same proposal blocks off `llm_extraction_data` (PR #1747); this card gives
 * the CSR the identical one-click actions without leaving the inbox:
 *
 *  - [Create in Sage & assign] → the standard AddCustomerModal, prefilled
 *    from the proposal and fully editable → POST /portal/customers/sage →
 *    the same customer-override path CustomerSearch uses (including the
 *    ReassignOrderConfirm linked-order safety valve).
 *  - "Did you mean …?" chips → one-click bind of a nearest-existing account
 *    (the THOMA01 near-name class), plus a Rebind chip for the match that
 *    was unbound in favor of the proposal.
 *  - [Create ship-to in Sage] → the standard NewSageShipToForm, prefilled —
 *    available once the email's linked draft order has a customer bound
 *    (a Sage ship-to is customer-scoped).
 *
 * Nothing auto-creates in Sage — the CSR click is the gate, and create
 * affordances follow sage_edit_ui_enabled like every other Sage write.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { portalService } from '../../services/portal';
import type { EmailProposals, LinkedOrderState } from '../../types/portal';
import AddCustomerModal from './AddCustomerModal';
import ReassignOrderConfirm from './ReassignOrderConfirm';
import NewSageShipToForm, { type CreatedShipTo } from './NewSageShipToForm';
import { needsOrderDecision } from './customerOverrideUtils';

interface ProposalCardProps {
  proposals: EmailProposals;
  /** The email whose customer gets overridden by the card's actions. */
  emailId: string;
  /** The email's linked EDITABLE draft order (when one exists) — enables the
   *  ship-to create, which is order/customer-scoped. */
  orderId?: string | null;
  orderCustomerNo?: string | null;
  /** A new Sage ship-to was created — the host binds it onto the draft. */
  onShipToCreated?: (addr: CreatedShipTo) => void;
  /** Refresh callback after an override/create lands. */
  onChanged?: () => void;
  onToast?: (message: string, kind: 'success' | 'error') => void;
}

/** Linked orders still needing a decision after an email-only override. */
interface PendingReassign {
  customerNo: string;
  customerName: string | null;
  linkedOrders: LinkedOrderState[];
}

const card: React.CSSProperties = {
  border: '1px solid #93c5fd',
  background: '#eff6ff',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 12.5,
  color: '#1e3a5f',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const btnPrimary: React.CSSProperties = {
  background: '#2563eb',
  border: '1px solid #2563eb',
  color: '#fff',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 12.5,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #93c5fd',
  color: '#1d4ed8',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  cursor: 'pointer',
};

const addressLines = (obj: {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string[] => {
  const cityLine = [obj.city, obj.state, obj.zip].filter(Boolean).join(', ');
  return [obj.address1, obj.address2, cityLine].filter(
    (l): l is string => Boolean(l && l.trim()),
  );
};

const ProposalCard: React.FC<ProposalCardProps> = ({
  proposals,
  emailId,
  orderId,
  orderCustomerNo,
  onShipToCreated,
  onChanged,
  onToast,
}) => {
  const [sageEnabled, setSageEnabled] = useState<boolean | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShipToForm, setShowShipToForm] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingReassign | null>(null);

  const customer = proposals.proposed_new_customer;
  const shipTo = proposals.proposed_new_ship_to;

  useEffect(() => {
    portalService
      .getPortalConfig()
      .then((cfg) => setSageEnabled(Boolean(cfg.sage_edit_enabled)))
      .catch(() => setSageEnabled(false));
  }, []);

  /** Bind a customer_no to the email via the standard override path. */
  const assignCustomer = useCallback(
    async (customerNo: string) => {
      setBusy(true);
      try {
        const resp = await portalService.overrideCustomer(emailId, customerNo);
        portalService.invalidateStatsCache();
        if (needsOrderDecision(resp.linked_orders)) {
          setPending({
            customerNo: resp.customer_no,
            customerName: resp.customer_name,
            linkedOrders: resp.linked_orders,
          });
          return;
        }
        onToast?.(`Customer set to ${customerNo}`, 'success');
        onChanged?.();
      } catch {
        onToast?.(`Failed to assign customer ${customerNo}`, 'error');
      } finally {
        setBusy(false);
      }
    },
    [emailId, onChanged, onToast],
  );

  const handleCustomerCreated = useCallback(
    (customerNo: string) => {
      setShowAddModal(false);
      void assignCustomer(customerNo);
    },
    [assignCustomer],
  );

  if (pending) {
    return (
      <ReassignOrderConfirm
        emailId={emailId}
        customerNo={pending.customerNo}
        customerName={pending.customerName}
        linkedOrders={pending.linkedOrders}
        onResolved={() => {
          setPending(null);
          onChanged?.();
        }}
        onToast={onToast}
      />
    );
  }

  const evidence = [customer?.evidence, shipTo?.evidence].filter(
    (e): e is string => Boolean(e && e.trim()),
  );

  return (
    <div data-testid="proposal-card" style={card}>
      {customer && (
        <>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1d4ed8' }}>
            ✨ Looks like a NEW customer
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }} data-testid="proposal-company-name">
              {customer.name}
            </div>
            {addressLines(customer).map((line) => (
              <div key={line} style={{ color: '#374151' }}>{line}</div>
            ))}
            {(customer.contact_name || customer.phone || customer.email) && (
              <div style={{ color: '#374151' }}>
                {[customer.contact_name, customer.phone, customer.email]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>

          {proposals.nearest_existing.length > 0 && (
            <div data-testid="proposal-nearest-existing">
              <div style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                Did you mean an existing account?
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {proposals.nearest_existing.map((hint) => (
                  <button
                    key={hint.customer_no}
                    type="button"
                    style={btnGhost}
                    disabled={busy}
                    onClick={() => void assignCustomer(hint.customer_no)}
                    title={[hint.city, hint.state].filter(Boolean).join(', ') || undefined}
                  >
                    {hint.customer_name || hint.customer_no} ({hint.customer_no})
                  </button>
                ))}
              </div>
            </div>
          )}

          {proposals.unbound_match?.customer_no && (
            <div style={{ fontSize: 11.5, color: '#92400e' }} data-testid="proposal-unbound-match">
              Originally matched to {proposals.unbound_match.customer_name || 'account'}{' '}
              ({proposals.unbound_match.customer_no}) — unbound pending review.{' '}
              <button
                type="button"
                style={{ ...btnGhost, padding: '2px 8px', fontSize: 11.5 }}
                disabled={busy}
                onClick={() => void assignCustomer(proposals.unbound_match!.customer_no!)}
              >
                Rebind
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              style={{ ...btnPrimary, opacity: busy || sageEnabled === false ? 0.5 : 1 }}
              disabled={busy || sageEnabled === false}
              onClick={() => setShowAddModal(true)}
              data-testid="proposal-create-customer-btn"
            >
              Create in Sage &amp; assign
            </button>
            {sageEnabled === false && (
              <span style={{ fontSize: 11.5, color: '#6b7280' }} data-testid="proposal-sage-disabled">
                Sage creation isn&apos;t enabled in this environment.
              </span>
            )}
          </div>
        </>
      )}

      {shipTo && (
        <div
          data-testid="proposal-ship-to"
          style={customer ? { borderTop: '1px solid #bfdbfe', paddingTop: 6 } : undefined}
        >
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1d4ed8' }}>
            ✨ Looks like a NEW ship-to address
          </div>
          <div style={{ color: '#374151' }}>
            {shipTo.name && <div style={{ fontWeight: 600 }}>{shipTo.name}</div>}
            {addressLines(shipTo).map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
          {orderId && orderCustomerNo && !showShipToForm && (
            <div style={{ marginTop: 4 }}>
              <button
                type="button"
                style={{ ...btnPrimary, opacity: busy || sageEnabled === false ? 0.5 : 1 }}
                disabled={busy || sageEnabled === false}
                onClick={() => setShowShipToForm(true)}
                data-testid="proposal-create-ship-to-btn"
              >
                Create ship-to in Sage
              </button>
              {sageEnabled === false && (
                <span style={{ fontSize: 11.5, color: '#6b7280', marginLeft: 8 }}>
                  Sage creation isn&apos;t enabled in this environment.
                </span>
              )}
            </div>
          )}
          {orderId && orderCustomerNo && showShipToForm && (
            <div style={{ marginTop: 6 }}>
              <NewSageShipToForm
                orderId={orderId}
                customerNo={orderCustomerNo}
                initial={{
                  name: shipTo.name ?? '',
                  address1: shipTo.address1 ?? '',
                  address2: shipTo.address2 ?? '',
                  city: shipTo.city ?? '',
                  state: shipTo.state ?? '',
                  zip: shipTo.zip ?? '',
                }}
                onCreated={(addr) => {
                  setShowShipToForm(false);
                  onShipToCreated?.(addr);
                  onChanged?.();
                }}
                onCancel={() => setShowShipToForm(false)}
              />
            </div>
          )}
          {orderId && !orderCustomerNo && (
            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
              Assign a customer first — the ship-to is created under the order&apos;s customer.
            </div>
          )}
          {!orderId && (
            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
              Open the order draft to create this ship-to in Sage.
            </div>
          )}
        </div>
      )}

      {evidence.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowEvidence((s) => !s)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1d4ed8',
              fontSize: 11.5,
              cursor: 'pointer',
              padding: 0,
            }}
            data-testid="proposal-evidence-toggle"
          >
            {showEvidence ? '▾ Hide evidence' : `▸ Why the AI thinks so (${evidence.length})`}
          </button>
          {showEvidence && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 16, color: '#4b5563' }}>
              {evidence.map((snippet) => (
                <li key={snippet} style={{ marginBottom: 2 }}>
                  {snippet}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showAddModal && customer && (
        <AddCustomerModal
          initialName={customer.name}
          initialEmail={customer.email ?? undefined}
          initialAddress1={customer.address1 ?? undefined}
          initialAddress2={customer.address2 ?? undefined}
          initialCity={customer.city ?? undefined}
          initialState={customer.state ?? undefined}
          initialZip={customer.zip ?? undefined}
          initialPhone={customer.phone ?? undefined}
          onCreated={handleCustomerCreated}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

export default ProposalCard;
